import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, MenuItem, Container } from "@mui/material";
import logoImage from "../../assets/images/logo.png";
import ReferralBanner from "./components/ReferralBanner";

// ─── Estilos compartidos ─────────────────────────────────────────────────────

const cardSx = {
  width: "100%",
  p: { xs: "20px", md: "28px" },
  borderRadius: { xs: "16px", md: "20px" },
  background: "#ffffff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 16px rgba(0,0,0,0.03), 0 12px 24px rgba(0,0,0,0.05)",
  border: "1px solid #eaeaea",
  boxSizing: "border-box",
};

const inputSx = {
  mb: 1,
  "& .MuiInputBase-root": {
    height: { xs: "44px", md: "46px" },
    backgroundColor: "#f7f9fc",
    fontFamily: "Quicksand-Regular",
    borderRadius: "10px",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e4e8ef" },
    "&:hover fieldset": { borderColor: "#a8b4c8" },
    "&.Mui-focused fieldset": { borderColor: "#1877f2", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { padding: "10px 14px", fontSize: { xs: "14px", md: "15px" } },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 11px) scale(1)",
    fontFamily: "Quicksand-Regular",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  "& .MuiInputLabel-asterisk": { display: "none" },
};

const btnSx = {
  mt: 2,
  height: { xs: "46px", md: "48px" },
  borderRadius: "10px",
  bgcolor: "#1877f2",
  fontFamily: "Quicksand-Bold",
  fontSize: { xs: "15px", md: "16px" },
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(24, 119, 242, 0.28)",
  transition: "all 0.2s ease",
  "&:hover": { bgcolor: "#166fe5", boxShadow: "0 6px 16px rgba(24, 119, 242, 0.38)" },
  "&.Mui-disabled": { bgcolor: "rgba(24, 119, 242, 0.35)", color: "white", boxShadow: "none" },
};

// ─── Paso 2: Información Personal ────────────────────────────────────────────

const PersonalInfo = ({ data, onNext, onBack }) => {
  const [nombres, setNombres]     = useState(data?.datos_profesional?.nombres || "");
  const [apellidos, setApellidos] = useState(data?.datos_profesional?.apellidos || "");
  const [profesion, setProfesion] = useState(data?.datos_profesional?.titulo_profesional || "");
  const [error, setError]         = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(
      nombres.length >= 2 && nombres.length <= 45 &&
      apellidos.length >= 2 && apellidos.length <= 45 &&
      profesion.length > 0
    );
  }, [nombres, apellidos, profesion]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) { setError("Por favor, completa todos los campos correctamente"); return; }
    onNext({ nombres, apellidos, profesion });
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      py: { xs: 3, md: 4 },
    }}>
      <Container maxWidth="xs" sx={{ px: { xs: 2, md: 2 } }}>
        <Box sx={cardSx}>
          <Box sx={{ mb: { xs: 2, md: 2.5 }, display: "flex", justifyContent: "center" }}>
            <img src={logoImage} alt="Clinifica" style={{ height: "34px" }} />
          </Box>

          <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "18px", md: "20px" }, color: "#1c1c1c", mb: "6px" }}>
            Hola {nombres || "👋"}
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, color: "#6b7280", mb: { xs: "20px", md: "24px" }, lineHeight: 1.5 }}>
            Encontramos estos datos en la Superintendencia de Salud. Por favor, ayúdanos a validarlos.
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Nombres" value={nombres} onChange={(e) => setNombres(e.target.value)} margin="normal" required inputProps={{ maxLength: 45 }} sx={inputSx} />
            <TextField fullWidth label="Apellidos" value={apellidos} onChange={(e) => setApellidos(e.target.value)} margin="normal" required inputProps={{ maxLength: 45 }} sx={inputSx} />
            <TextField
              fullWidth select label="Profesión principal" value={profesion}
              onChange={(e) => setProfesion(e.target.value)} margin="normal" required
              sx={{ ...inputSx, "& .MuiSelect-select": { fontFamily: "Quicksand-Regular", fontSize: { xs: "14px", md: "15px" } } }}
            >
              {data?.titulos_disponibles?.map((titulo) => (
                <MenuItem key={titulo} value={titulo} sx={{ fontFamily: "Quicksand-Regular", fontSize: "14px" }}>{titulo}</MenuItem>
              ))}
            </TextField>

            {error && (
              <Typography sx={{ color: "#d32f2f", fontSize: { xs: "12px", md: "13px" }, fontFamily: "Quicksand-Regular", textAlign: "center", mt: "12px", p: "12px", bgcolor: "rgba(211, 47, 47, 0.07)", borderRadius: "10px" }}>
                {error}
              </Typography>
            )}

            <Button type="submit" fullWidth variant="contained" disabled={!isFormValid} sx={btnSx}>
              Continuar
            </Button>
          </form>

          <Button variant="text" onClick={onBack} sx={{ color: "#6b7280", textTransform: "none", fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, mt: 1, p: 0 }}>
            volver
          </Button>
        </Box>

        <Box sx={{ mt: "12px" }}>
          <ReferralBanner />
        </Box>
      </Container>
    </Box>
  );
};

export default PersonalInfo;
